import formatCurrency from "constants/formatCurrency";
import formatDate from "constants/formatDate";
import { addLeasePayment, deleteLeasePayment, editLeasePayment } from "helpers/fakebackend_helper";
import React, { useEffect, useState } from "react";
import { getReportsForLocation } from "helpers/fakebackend_helper";
import DateRangeSelector from "components/Common/DateRangeSelector";
import {
    Col,
    Button,
    Table,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Input,
    InputGroup,
    InputGroupText,
    Row,
} from "reactstrap";

const LeasePayments = ({ location_id, lease_amount, operation_hours, payments, handleSave }) => {
    const user = JSON.parse(localStorage.getItem("authUser"));
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPayment, setEditingPayment] = useState(null);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [reports, setReports] = useState(null);
    const [dayRows, setDayRows] = useState([]);
    const [meterAdjustment, setMeterAdjustment] = useState("0");
    const [eventsAdjustment, setEventsAdjustment] = useState("0");
    const [validationsAdjustment, setValidationsAdjustment] = useState("0");
    const [tipAdjustment, setTipAdjustment] = useState("0");
    const [ccAdjustment, setCcAdjustment] = useState("0");
    const [leaseSettlement, setLeaseSettlement] = useState("0.00");
    const [formData, setFormData] = useState({
        date: new Date().toISOString().slice(0, 10),
        amount: "0.00",
        note: "",
    });
    const totalOpen = dayRows.filter(r => r.open).length;

    const toNumber = (v) => {
        if (v === null || v === undefined) return 0;
        const n = String(v).replace(/[^0-9.-]/g, "");
        return n === "" || n === "-" ? 0 : Number(n);
    };

    const onMoneyChange = (setter) => (e) => {
        const val = e.target.value;
        if (/^-?\d*(\.\d{0,2})?$/.test(val)) {
            setter(val);
        }
    };

    const toggleModal = () => {
        if (isModalOpen) {
            // Reset all state when closing
            setEditingPayment(null);
            setStartDate("");
            setEndDate("");
            setReports(null);
            setDayRows([]);
            setMeterAdjustment("0");
            setEventsAdjustment("0");
            setValidationsAdjustment("0");
            setTipAdjustment("0");
            setCcAdjustment("0");
            setLeaseSettlement("0.00");
            setFormData({
                date: new Date().toISOString().slice(0, 10),
                amount: "0.00",
                note: "",
            });
        }
        setIsModalOpen(!isModalOpen);
    };

    const loadReports = async (s, e) => {
        setStartDate(s);
        setEndDate(e);

        const end = new Date(e);
        end.setDate(end.getDate());
        const adjustedDate = end.toISOString().slice(0, 10);
        setFormData(prev => ({ ...prev, date: adjustedDate }));

        console.log("s:", s)
        console.log("e:", e)

        const res = await getReportsForLocation(user.token, {
            start_date: s,
            end_date: e,
            location_id,
        });
        setReports(res.data.corepark);
    };

    useEffect(() => {
        if (startDate && endDate) {
            const daysMap = parseOperationHours(operation_hours);
            const rows = [];
            const start = toLocalDate(startDate);
            const end = toLocalDate(endDate);

            for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                const weekday = d.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
                const isOpen = daysMap[weekday];
                rows.push({
                    date: new Date(d),
                    open: isOpen, // boolean
                });
            }
            setDayRows(rows);
        }
    }, [startDate, endDate, operation_hours]);

    const toggleDay = (idx) => {
        setDayRows(prev =>
            prev.map((r, i) =>
                i === idx ? { ...r, open: !r.open } : r
            )
        );
    };

    useEffect(() => {
        if (isModalOpen && !startDate && !endDate) {
            setReports(null);
        }
    }, [isModalOpen]);

    useEffect(() => {
        if (!reports) return;

        const cardUsed = toNumber(
            ccAdjustment || (
                reports.square.square_total -
                reports.square.net_trx_fee_amount -
                reports.square.total_card_tax_amount -
                reports.square.total_refund_card_tax_amount
            )
        );
        const tipUsed = toNumber(tipAdjustment || reports.tip || 0);

        const totalLease = lease_amount * totalOpen;
        const rawSettlement = totalLease - cardUsed - tipUsed - reports.square.refunds_total;
        const adjustedSettlement =
            (lease_amount * totalOpen)
            - cardUsed
            - tipUsed
            - (reports.square.refunds_total)
            - toNumber(meterAdjustment)
            - toNumber(eventsAdjustment)
            - toNumber(validationsAdjustment);

        // Calculate settlement but only update if not editing or amount is undefined/0
        setLeaseSettlement(adjustedSettlement.toFixed(2));
        setFormData(p => ({
            ...p,
            amount: editingPayment && toNumber(p.amount) > 0 ? p.amount : adjustedSettlement.toFixed(2),
        }));

        const updateTag = (note, label, value) => {
            const regex = new RegExp(`\\{${label}: \\$[0-9.,]+\\}`, "i");
            const tag = `{${label}: $${Number(value).toFixed(2)}}`;

            if (Number(value) === 0) {
                // remove tag if value is zero
                return note.replace(regex, "").trim();
            }

            if (regex.test(note)) {
                // replace existing tag
                return note.replace(regex, tag).trim();
            } else {
                // add new tag
                return (note.trim() + " " + tag).trim();
            }
        };

        setFormData(prev => {
            let note = prev.note || "";

            if (tipAdjustment && tipAdjustment != reports.tip)
                note = updateTag(note, "Tip Adjustment", tipAdjustment);
            else note = updateTag(note, "Tip Adjustment", 0);

            note = updateTag(note, "Meter Adjustment", meterAdjustment);
            note = updateTag(note, "Events Adjustment", eventsAdjustment);
            note = updateTag(note, "Validations Adjustment", validationsAdjustment);
            if (ccAdjustment && ccAdjustment != (reports.square.square_total - reports.square.net_trx_fee_amount - reports.square.total_card_tax_amount - reports.square.total_refund_card_tax_amount))
                note = updateTag(note, "CC Adjustment", ccAdjustment);
            else note = updateTag(note, "CC Adjustment", 0);

            // clean multiple spaces
            note = note.replace(/\s{2,}/g, " ").trim();

            return { ...prev, note };
        });
    }, [
        reports,
        meterAdjustment,
        eventsAdjustment,
        tipAdjustment,
        ccAdjustment,
        validationsAdjustment,
        totalOpen,
        lease_amount,
    ]);

    useEffect(() => {
        if (!reports) return;

        const defaultCC =
            reports.square.square_total -
            reports.square.net_trx_fee_amount -
            reports.square.total_card_tax_amount -
            reports.square.total_refund_card_tax_amount;

        setCcAdjustment(defaultCC.toFixed(2));
        setTipAdjustment(Number(reports.tip || 0).toFixed(2));
    }, [reports]);

    const handleAddClick = () => {
        if (!operation_hours) {
            alert("Please add hours to this location!")
            return;
        }

        setEditingPayment(null);
        setFormData({
            date: new Date().toISOString().slice(0, 10),
            amount: 0,
            note: "",
        });
        setMeterAdjustment(0);
        setEventsAdjustment(0);

        toggleModal();
    }

    const handleEditClick = (payment) => {
        setEditingPayment(payment);
        setMeterAdjustment(payment.meter_adjustment || 0);
        setEventsAdjustment(payment.events_adjustment || 0);
        setValidationsAdjustment(payment.validations_adjustment || 0);
        setFormData({
            date: payment.payment_date,
            amount: payment.amount,
            note: payment.note || "",
        });
        console.log("payment: ", payment)
        toggleModal();
    };

    const handleDeleteClick = async (id) => {
        await deleteLeasePayment(user.token, id);
        handleSave()
    };

    const onSave = async () => {
        const payload = {
            ...formData,
            amount: toNumber(formData.amount),
            location_id,
            meter_adjustment: toNumber(meterAdjustment),
            events_adjustment: toNumber(eventsAdjustment),
            validations_adjustment: toNumber(validationsAdjustment),
            tip_adjustment: toNumber(tipAdjustment),
            cc_adjustment: toNumber(ccAdjustment),
        };

        if (editingPayment) {
            await editLeasePayment(user.token, editingPayment.id, payload);
        } else {
            await addLeasePayment(user.token, payload);
        }

        toggleModal();
        handleSave();
    };

    function parseOperationHours(hours) {
        const days = [
            "monday",
            "tuesday",
            "wednesday",
            "thursday",
            "friday",
            "saturday",
            "sunday",
        ];

        const result = {};
        days.forEach(day => {
            const regex = new RegExp(day, "i");
            const line = hours.split("\n").find(l => regex.test(l));
            result[day] = line && !line.toLowerCase().includes("closed");
        });
        return result;
    }

    function toLocalDate(dateStr) {
        const [y, m, d] = dateStr.split("-").map(Number);
        return new Date(y, m - 1, d);
    }

    return (
        <div>
            <Button className="button-gradient-bg mb-3" onClick={handleAddClick}>
                Add Lease Payment
            </Button>

            <Table className="align-middle table-hover table-dark table-nowrap mb-0">
                <thead className="table-success">
                    <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Note</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {payments && payments.length > 0 ? (
                        payments.map((payment) => (
                            <tr key={payment.id}>
                                <td>{formatDate(payment.payment_date)}</td>
                                <td>${formatCurrency(payment.amount)}</td>
                                <td title={payment.note}>
                                    {payment.note && payment.note.length > 70
                                        ? `${payment.note.substring(0, 70)}...`
                                        : payment.note}
                                </td>
                                <td>
                                    <Button
                                        color="link"
                                        className="p-0 me-2"
                                        onClick={() => handleEditClick(payment)}
                                    >
                                        <i className="bx bx-pencil"></i>
                                    </Button>
                                    <Button
                                        color="link"
                                        className="p-0 text-danger"
                                        onClick={() => handleDeleteClick(payment.id)}
                                    >
                                        <i className="bx bx-trash"></i>
                                    </Button>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={4} className="text-center text-muted">
                                No lease payments
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>

            {/* Add/Edit Modal */}
            <Modal isOpen={isModalOpen} toggle={toggleModal} size="lg">
                <ModalHeader toggle={toggleModal}>
                    {editingPayment ? "Edit Lease Payment" : "Add Lease Payment"}
                </ModalHeader>
                <ModalBody>
                    {/* Date Range Selector */}
                    <Col xl="auto" className="mb-3">
                        <DateRangeSelector onApply={(s, e) => loadReports(s, e)} />
                    </Col>

                    <Col xl="auto" className="mb-3 text-center">
                        {reports && (
                            (() => {
                                return (
                                    <>
                                        <h4 className="mb-4 d-flex justify-content-center align-items-center flex-wrap gap-2">
                                            <span>CC:</span>
                                            <InputGroup style={{ width: "120px" }}>
                                                <InputGroupText>$</InputGroupText>
                                                <Input
                                                    type="text"
                                                    value={ccAdjustment}// || (reports.square.square_total - reports.square.net_trx_fee_amount - reports.square.total_card_tax_amount - reports.square.total_refund_card_tax_amount) || 0
                                                    onChange={onMoneyChange(setCcAdjustment)}
                                                />
                                                {/* <Input
                                                    type="number"
                                                    value={ccAdjustment || (reports.square.square_total - reports.square.net_trx_fee_amount - reports.square.total_card_tax_amount - reports.square.total_refund_card_tax_amount) || 0}
                                                    onChange={(e) => setCcAdjustment(e.target.value)}
                                                /> */}
                                            </InputGroup>
                                            <span>+ Tip:</span>
                                            <InputGroup style={{ width: "120px" }}>
                                                <InputGroupText>$</InputGroupText>
                                                <Input
                                                    type="text"
                                                    value={tipAdjustment} // || reports.tip || 0
                                                    onChange={onMoneyChange(setTipAdjustment)}
                                                />
                                                {/* <Input
                                                    type="number"

                                                    onChange={(e) => setTipAdjustment(e.target.value)}
                                                /> */}
                                            </InputGroup>
                                            <span>
                                                - Refunds: ${formatCurrency(reports.square.refunds_total)} =
                                            </span>
                                            <span>
                                                ${formatCurrency(
                                                    Number(ccAdjustment || (reports.square.square_total - reports.square.net_trx_fee_amount - reports.square.total_card_tax_amount - reports.square.total_refund_card_tax_amount) || 0) +
                                                    Number(tipAdjustment || reports.tip || 0) -
                                                    (reports.square.refunds_total)
                                                )}
                                            </span>
                                        </h4>

                                        <h4>Daily Lease Amount: ${formatCurrency(lease_amount)}</h4>
                                    </>
                                );
                            })()
                        )}
                    </Col>

                    {startDate && endDate && reports && (
                        (() => {
                            const daysMap = parseOperationHours(operation_hours);
                            const rows = [];
                            const start = toLocalDate(startDate);
                            const end = toLocalDate(endDate);

                            for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                                const weekday = d.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
                                const isOpen = daysMap[weekday];
                                rows.push({
                                    date: new Date(d),
                                    open: isOpen ? "Open" : "Closed",
                                });
                            }

                            return (
                                dayRows.length > 0 && (
                                    <>
                                        <h1 className="mb-2 text-center">Total Open Days: {totalOpen}</h1>
                                        <h3 className="mb-2 text-center">
                                            Total Lease Amount: ${formatCurrency((lease_amount) * totalOpen)}
                                        </h3>

                                        <div className="d-flex justify-content-center align-items-center gap-3 my-3">
                                            <div>
                                                <label>Meter Adjustment</label>
                                                <InputGroup>
                                                    <InputGroupText>$</InputGroupText>
                                                    <Input
                                                        type="text"
                                                        value={meterAdjustment}
                                                        onChange={onMoneyChange(setMeterAdjustment)}
                                                    />
                                                    {/* <Input
                                                        type="number"
                                                        step="0.01"
                                                        value={meterAdjustment}
                                                        onChange={(e) => setMeterAdjustment(e.target.value)}
                                                    /> */}
                                                </InputGroup>
                                            </div>

                                            <div>
                                                <label>Events Adjustment</label>
                                                <InputGroup>
                                                    <InputGroupText>$</InputGroupText>
                                                    <Input
                                                        type="text"
                                                        value={eventsAdjustment}
                                                        onChange={onMoneyChange(setEventsAdjustment)}
                                                    />
                                                    {/* <Input
                                                        type="number"
                                                        step="0.01"
                                                        value={eventsAdjustment}
                                                        onChange={(e) => setEventsAdjustment(e.target.value)}
                                                    /> */}
                                                </InputGroup>
                                            </div>

                                            <div>
                                                <label>Validations Adjustment</label>
                                                <InputGroup>
                                                    <InputGroupText>$</InputGroupText>
                                                    <Input
                                                        type="text"
                                                        value={validationsAdjustment}
                                                        onChange={onMoneyChange(setValidationsAdjustment)}
                                                    />
                                                    {/* <Input
                                                        type="number"
                                                        step="0.01"
                                                        value={validationsAdjustment}
                                                        onChange={(e) => setValidationsAdjustment(e.target.value)}
                                                    /> */}
                                                </InputGroup>
                                            </div>
                                        </div>

                                        <h1 className="mb-2 text-center">
                                            Lease Settlement: ${formatCurrency(leaseSettlement)}
                                        </h1>

                                        {(() => {
                                            // Split into chunks of 7 and keep the original index
                                            const chunked = [];
                                            for (let i = 0; i < dayRows.length; i += 8) {
                                                const slice = dayRows.slice(i, i + 8).map((item, idx) => ({
                                                    ...item,
                                                    absIndex: i + idx, // absolute index in dayRows
                                                }));
                                                chunked.push(slice);
                                            }

                                            // Split into pairs of 2 tables per row
                                            const pairs = [];
                                            for (let i = 0; i < chunked.length; i += 2) {
                                                pairs.push(chunked.slice(i, i + 2));
                                            }

                                            return (
                                                <>
                                                    {pairs.map((pair, rowIdx) => (
                                                        <Row key={rowIdx} className="mb-3">
                                                            {pair.map((chunk, tblIdx) => (
                                                                <Col md={6} key={tblIdx}>
                                                                    <Table borderless size="sm" className="mt-3">
                                                                        <thead>
                                                                            <tr>
                                                                                <th>Date</th>
                                                                                <th>Open/Close</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {chunk.map(r => (
                                                                                <tr key={r.absIndex}>
                                                                                    <td>
                                                                                        {r.date.toLocaleDateString("en-US", { month: "short", day: "numeric" })},{" "}
                                                                                        {r.date.toLocaleDateString("en-US", { weekday: "long" })}
                                                                                    </td>
                                                                                    <td>
                                                                                        <Button
                                                                                            size="sm"
                                                                                            color={r.open ? "success" : "secondary"}
                                                                                            onClick={() => toggleDay(r.absIndex)}
                                                                                        >
                                                                                            {r.open ? "Open" : "Closed"}
                                                                                        </Button>
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </Table>
                                                                </Col>
                                                            ))}
                                                        </Row>
                                                    ))}
                                                </>
                                            );
                                        })()}
                                    </>
                                )
                            );
                        })()
                    )}

                    <label>Date</label>
                    <Input
                        type="date"
                        value={formData.date}
                        onChange={(e) =>
                            setFormData({ ...formData, date: e.target.value })
                        }
                    />

                    <label className="mt-3">Amount</label>
                    <InputGroup>
                        <InputGroupText>$</InputGroupText>
                        <Input
                            type="text"
                            value={formData.amount}
                            onChange={(e) =>
                                onMoneyChange((v) => setFormData(p => ({ ...p, amount: v })))(e)
                            }
                        />
                        {/* <Input
                            type="number"
                            value={formData.amount}
                            onChange={(e) =>
                                setFormData({ ...formData, amount: e.target.value })
                            }
                        /> */}
                    </InputGroup>

                    <label className="mt-3">Note</label>
                    <Input
                        type="text"
                        value={formData.note}
                        onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                        placeholder="Optional note"
                    />
                </ModalBody>
                <ModalFooter>
                    <Button className="button-gradient-bg" onClick={onSave}>
                        Save
                    </Button>
                    <Button color="secondary" onClick={toggleModal}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default LeasePayments;